// frontend/app/utils/orderCalculations.ts
import type {OrderLine} from '../transport/apiServiceResponse';

// ============ Type Definitions ============

/**
 * Order adjustment from API (order-level discounts, coupons, deals)
 * Note: This mirrors the local interface in cartStore.ts
 */
export interface OrderAdjustment {
  name: string;
  amount: {
    cents_inc_tax: number;
  };
}

/**
 * Discount/credit from API
 */
export interface DiscountCredit {
  narrative: string;
  amount: number; // Raw cents, positive for a reduction (measured 2026-08-24)
  /**
   * MFL's own classification of the line, `'coupon'` for a coupon discount
   * (measured 2026-08-24). Optional because `apiServiceResponse` types
   * `discounts_and_credits` as `any[]` and passes MFL's JSON through unparsed,
   * so nothing enforces the field's presence at the boundary.
   */
  type?: string;
}

/**
 * Generic name + amount for display
 */
export interface NamedAmount {
  name: string;
  amount: number; // cents
}

// ============ Fee Detection ============

/** Fee name patterns for normalized matching (lowercase) */
export const FEE_NAMES = {
  DELIVERY: 'delivery fee',
  PICKUP: 'free pickup',
} as const;

/**
 * Normalize string for fee comparison: lowercase + trim
 */
const normalizeFeeName = (name: string | undefined): string => (name ?? '').toLowerCase().trim();

/**
 * Check if normalized name matches expected fee name
 */
const matchesFeeName = (name: string | undefined, expected: string): boolean =>
  normalizeFeeName(name).includes(expected);

/**
 * Check if order line is a delivery fee charge
 * Uses normalized match (lowercase, trimmed, includes)
 */
export const isDeliveryFee = (line: OrderLine): boolean =>
  line.charge === true && matchesFeeName(line.name, FEE_NAMES.DELIVERY);

/**
 * Check if order line is a pickup fee charge
 * Uses normalized match (lowercase, trimmed, includes)
 */
export const isPickupFee = (line: OrderLine): boolean =>
  line.charge === true && matchesFeeName(line.name, FEE_NAMES.PICKUP);

/**
 * Check if order line is any charge (fee) line
 */
export const isChargeLine = (line: OrderLine): boolean => line.charge === true;

// ============ Order Line Filtering ============

/**
 * Get product lines (non-charge lines)
 */
export const getProductLines = (lines: OrderLine[] | undefined): OrderLine[] =>
  lines?.filter(line => !line.charge) ?? [];

/**
 * Get charge lines (fees)
 */
export const getChargeLines = (lines: OrderLine[] | undefined): OrderLine[] =>
  lines?.filter(line => line.charge) ?? [];

// ============ Calculations ============

/**
 * Calculate subtotal from product lines
 * Uses subtotal.cents_inc_tax (before line adjustments)
 */
export const calculateSubtotal = (lines: OrderLine[] | undefined): number =>
  getProductLines(lines)
    .map(line => line.subtotal?.cents_inc_tax ?? 0)
    .reduce((a, b) => a + b, 0);

/**
 * Get total for a charge line
 * Uses total.cents_inc_tax (after any line adjustments)
 */
export const getChargeLineAmount = (line: OrderLine): number => line.total?.cents_inc_tax ?? 0;

// ============ Adjustment Filtering ============

/**
 * Filter adjustments to exclude delivery-related ones
 * Maps to NamedAmount for display
 */
export const getNonDeliveryAdjustments = (
  adjustments: OrderAdjustment[] | undefined,
): NamedAmount[] =>
  adjustments
    ?.filter(adj => !!adj.name && !adj.name.toLowerCase().includes('delivery'))
    .map(adj => ({
      name: adj.name,
      amount: adj.amount.cents_inc_tax,
    })) ?? [];

/**
 * Map discounts_and_credits to display format
 * Negates amount for display (shows as reduction)
 */
export const getDiscountsAndCredits = (discounts: DiscountCredit[] | undefined): NamedAmount[] =>
  discounts?.map(dc => ({
    name: dc.narrative,
    amount: -1 * dc.amount,
  })) ?? [];

// ============ Uncounted discounts (DIS-1608) ============

/**
 * A discount MFL has already deducted from `balance_at_checkout` but will not
 * honour: an expired exclusive offer, or a coupon whose basket does not qualify.
 * Adding these back yields the total the shopper will actually pay.
 *
 * Offers live in `adjustments`, coupons in `discounts_and_credits` (measured
 * 2026-08-21). Matching therefore keys on the array plus, for coupons, the
 * order's `coupon_code` — a coupon and an offer of the same name no longer
 * collide the way the old lower-cased-name matchers allowed.
 */
export type UncountedDiscountsInput = {
  adjustments: OrderAdjustment[] | undefined;
  discounts: DiscountCredit[] | undefined;
  /** Lower-cased headline of the expired offer, or null when no offer expired. */
  expiredOfferName: string | null;
  /** `coupon_code` when the coupon is not qualified, else null. */
  uncountedCouponCode: string | null;
};

/**
 * The `delivery` exclusion is not a heuristic about offer names: it mirrors the
 * render filter in OrderTotalsLoyaltyEnabled, which drops delivery adjustments
 * from the adjustments list entirely. A line that is never drawn must never be
 * added back, or the total would gain an amount with no row explaining it.
 * Keep the two predicates in step.
 */
export const isUncountedOfferAdjustment = (
  adj: OrderAdjustment,
  expiredOfferName: string | null,
): boolean =>
  !!expiredOfferName &&
  !!adj.name &&
  !adj.name.toLowerCase().includes('delivery') &&
  adj.name.toLowerCase().includes(expiredOfferName);

/**
 * Measured 2026-08-24 against a live MFL order carrying coupon `%OFF`: the
 * discount line is `{type: 'coupon', narrative: 'Coupon %OFF', amount: 800}`,
 * and `balance_at_checkout` (7196) is `total` (7996) less that 800. So `type`
 * is MFL's own classification and is what this keys on. An order holds one
 * `coupon_code` at a time, so a single coupon-typed line is unambiguous — no
 * need to parse the code back out of a sentence.
 *
 * The narrative fallback covers only a response with `type` absent. It is kept
 * because `discounts_and_credits` is typed `any[]` at the transport boundary,
 * so a missing field would not fail loudly — it would silently sum 0 and show
 * the discounted total while checkout is blocked. Its `coupon <code>` form
 * matches the one narrative measured, but the codes seen include `%OFF`, so
 * treat substring matching as the weaker path, not the contract.
 */
export const isUncountedCouponLine = (
  dc: DiscountCredit,
  uncountedCouponCode: string | null,
): boolean => {
  if (!uncountedCouponCode) return false;
  if (dc.type) return dc.type.toLowerCase() === 'coupon';
  return (
    !!dc.narrative &&
    dc.narrative.toLowerCase().includes(`coupon ${uncountedCouponCode.toLowerCase()}`)
  );
};

export const sumUncountedDiscounts = ({
  adjustments,
  discounts,
  expiredOfferName,
  uncountedCouponCode,
}: UncountedDiscountsInput): number => {
  const offerCents =
    adjustments
      ?.filter(adj => isUncountedOfferAdjustment(adj, expiredOfferName))
      .reduce((total, adj) => total + Math.abs(adj.amount.cents_inc_tax), 0) ?? 0;

  const couponCents =
    discounts
      ?.filter(dc => isUncountedCouponLine(dc, uncountedCouponCode))
      .reduce((total, dc) => total + Math.abs(dc.amount), 0) ?? 0;

  return offerCents + couponCents;
};
